import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import useContentIndex from '../../hooks/useContentIndex';
import './DataItemPage.css';

const stripFrontmatter = (value) => value.replace(/^---[\s\S]*?---\s*/m, '');
const stripMarkdown = (value) => stripFrontmatter(value)
  .replace(/```[\s\S]*?```/g, '')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/^#{1,6}\s+/gm, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/[*_~]/g, '')
  .replace(/\s+/g, ' ')
  .trim();
const formatDate = (date) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${date}T00:00:00`));

const YoutubeEmbed = ({ url }) => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^#&?\s]+)/);
  if (!match?.[1]) return <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>;
  return <div className="youtube-embed"><iframe src={`https://www.youtube.com/embed/${match[1]}`} title="Embedded YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
};

const ThoughtPage = () => {
  const { slug } = useParams();
  const { items, loading: indexLoading } = useContentIndex({ includeUnlisted: true });
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [progress, setProgress] = useState(0);
  const sessionRef = useRef(0);
  const item = items.find((entry) => entry.slug === slug || String(entry.id) === slug) || null;

  useEffect(() => {
    let mounted = true;
    if (!item) {
      if (!indexLoading) setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetch(item.path, { cache: 'no-store' })
      .then((response) => { if (!response.ok) throw new Error(); return response.text(); })
      .then((text) => { if (mounted) setMarkdown(text); })
      .catch(() => { if (mounted) setMarkdown(''); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => {
      mounted = false;
      sessionRef.current += 1;
      window.speechSynthesis?.cancel();
    };
  }, [item, indexLoading]);

  useEffect(() => {
    const scroller = document.getElementById('main-content');
    if (!scroller) return undefined;
    const update = () => {
      const distance = scroller.scrollHeight - scroller.clientHeight;
      setProgress(distance > 0 ? Math.min(100, Math.max(0, Math.round(scroller.scrollTop / distance * 100))) : 0);
    };
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { scroller.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [item, loading]);

  const toggleSpeech = () => {
    const synth = window.speechSynthesis;
    if (!synth || !markdown) return;
    if (speaking || synth.speaking) {
      sessionRef.current += 1;
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const words = stripMarkdown(markdown).split(' ');
    const chunks = [];
    for (let i = 0; i < words.length; i += 38) chunks.push(words.slice(i, i + 38).join(' '));
    const session = sessionRef.current + 1;
    sessionRef.current = session;
    let index = 0;
    const next = () => {
      if (sessionRef.current !== session || index >= chunks.length) {
        setSpeaking(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(chunks[index]);
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => { index += 1; next(); };
      utterance.onerror = () => setSpeaking(false);
      synth.speak(utterance);
    };
    synth.cancel();
    next();
  };

  if (loading || indexLoading) return <div className="thought-state"><p className="record-kicker">Blog / retrieving</p><h1>Pulling the record…</h1></div>;
  if (!item) return <div className="thought-state"><p className="record-kicker">404 / observation file</p><h1>Article not found.</h1><Link className="record-action" to="/blog">Return to blog</Link></div>;

  const reference = `OBS—${String(item.id).padStart(3, '0')}`;

  const related = items.filter((entry) => entry.id !== item.id && !entry.unlisted).map(entry => ({ entry, score: (entry.topics || []).filter(topic => item.topics?.includes(topic)).length })).sort((a, b) => b.score - a.score).slice(0, 2);

  return <article className="thought-file">
    <div className="reading-progress" role="progressbar" aria-label="Reading progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}><span style={{ width: `${progress}%` }} /></div>
    <Link className="file-return" to="/blog">← Back to all articles</Link>
    <header className="thought-file__header">
      <div>
        <p className="record-kicker">Blog / {reference}</p>
        <h1>{item.title}</h1>
        <p className="thought-file__deck">{item.summary}</p>
        <div className="thought-file__topics">{item.topics?.map((topic) => <span key={topic}>{topic}</span>)}</div>
      </div>
      <aside className="thought-file__docket">
        <span>Filed <strong>{formatDate(item.date)}</strong></span>
        {item.updated && <span>Revised <strong>{formatDate(item.updated)}</strong></span>}
        <span>State <strong>{item.maturity || item.type}</strong></span>
        <span>Length <strong>{item.readingMinutes || 1} min.</strong></span>
        <button type="button" onClick={toggleSpeech}>{speaking ? 'Stop reading' : 'Listen to article'}</button>
      </aside>
    </header>
    <div className="reader-heading"><span>THE READING ROOM</span><span>{item.readingMinutes || 1} MIN READ / {reference}</span></div>
    <div className="thought-file__body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
          p: ({ children }) => {
            const text = React.Children.toArray(children).filter((child) => typeof child === 'string').join(' ');
            const match = text.match(/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s)]+/);
            return <><p>{children}</p>{match && <YoutubeEmbed url={match[0]} />}</>;
          },
        }}
      >{stripFrontmatter(markdown)}</ReactMarkdown>
    </div>
    <footer className="reader-end"><div><span>END OF ARTICLE / {reference}</span><h2>Keep exploring<span>.</span></h2><p>More notes and ideas from the archive.</p></div><Link to="/blog">Browse all articles ↗</Link></footer>
    <div className="reader-related">{related.map(({ entry }) => <Link to={`/blog/${entry.slug}`} key={entry.id}><span>{entry.topics?.slice(0, 2).join(' / ')}</span><strong>{entry.title}</strong><small>{entry.readingMinutes || 1} MIN READ <b>↗</b></small></Link>)}</div>
  </article>;
};

export default ThoughtPage;
